import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api";
import { StatCard } from "@/components/reports/stat-card";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/reports/subs")({
  component: SubsReport,
});

interface SubRow {
  userId: string | null;
  userName: string | null;
  companyName: string | null;
  projectName: string;
  flhaCount: number;
  lastFlhaDate: string | null;
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysBetween(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

function SubsReport() {
  const params = new URLSearchParams(window.location.search);
  const dateFrom = params.get("dateFrom") || daysAgo(30);
  const dateTo = params.get("dateTo") || today();
  const projectId = params.get("projectId") || undefined;

  const qs = new URLSearchParams({ dateFrom, dateTo });
  if (projectId) qs.set("projectId", projectId);

  const { data, isLoading } = useQuery({
    queryKey: ["reports", "sub-completion", { dateFrom, dateTo, projectId }],
    queryFn: () =>
      apiGet<{ data: SubRow[] }>(
        `/api/reports/sub-completion?${qs.toString()}`
      ),
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-16 animate-pulse bg-muted/40 rounded" />
        ))}
      </div>
    );
  }

  const subs = data?.data ?? [];
  const activeSubs = subs.filter((s) => s.userId);
  const avgFlhas =
    activeSubs.length > 0
      ? Math.round(
          activeSubs.reduce((s, r) => s + r.flhaCount, 0) / activeSubs.length
        )
      : 0;

  if (subs.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">
        No subcontractors found for the selected period.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard label="Active Subs" value={activeSubs.length} />
        <StatCard label="Avg FLHAs / Sub" value={avgFlhas} />
      </div>

      {/* Sub list */}
      <div>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Subcontractor Compliance
        </h3>
        <div className="border rounded-lg divide-y">
          {subs.map((sub, i) => {
            const daysAgoVal = daysBetween(sub.lastFlhaDate);
            const statusColor =
              sub.flhaCount === 0
                ? "text-red-600"
                : daysAgoVal !== null && daysAgoVal <= 7
                  ? "text-green-600"
                  : "text-amber-600";

            const statusLabel =
              sub.flhaCount === 0
                ? "No FLHAs"
                : daysAgoVal !== null && daysAgoVal <= 7
                  ? "Active"
                  : `${daysAgoVal ?? "?"}d ago`;

            return (
              <div
                key={`${sub.userId}-${sub.projectName}-${i}`}
                className="flex items-center justify-between px-3 py-2.5 text-sm"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">
                      {sub.userName ?? "Pending"}
                    </span>
                    {sub.companyName && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded shrink-0">
                        {sub.companyName}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {sub.projectName}
                  </span>
                </div>
                <div className="flex items-center gap-3 shrink-0 ml-2">
                  <span className="font-semibold tabular-nums">
                    {sub.flhaCount}
                  </span>
                  <span
                    className={cn(
                      "text-xs font-medium min-w-[60px] text-right",
                      statusColor
                    )}
                  >
                    {statusLabel}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
